// Canonical Redux store types for the web apps.
//
// This is the single source of truth for the shape of the Redux store. Both
// `apps/app` and `apps/website` import these instead of re-declaring a local
// `interface RootState` per file or falling back to `useSelector((s: any) => …)`.
// Domain shapes are reused from `@lemonadetraining/contracts` so the store agrees
// with the published data model rather than duplicating it.

import type { User as FirebaseUser, UserCredential } from "firebase/auth";
import type {
  AccessNotice,
  BoxRole,
  BoxMemberStatus,
  CustomClaims,
  Price,
  ProgrammingType,
  TimeFormatPreference,
  TimeZoneDisplayPreference,
} from "@lemonadetraining/contracts";

/* ------------------------------ notify slice ------------------------------ */

// Joy/MUI severity strings the notification slice stores (see NOTIFICATION_TYPE
// in ./notification-slice): info→"primary", warning→"warning", error→"danger",
// success→"success".
export type NotificationSeverity = "primary" | "warning" | "danger" | "success";

export interface NotificationState {
  severity: NotificationSeverity;
  title: string;
  message: string;
  activate: boolean;
}

/* ------------------------------- cart slice ------------------------------- */

export interface CartItem {
  programUID: string;
  priceID: string;
  pricing: Price;
  stripeAccountID?: string | null;
  isLemonadeProduct?: boolean;
  programmingType?: { type: ProgrammingType };
  // Display fields read by the cart/product UI.
  name?: string;
  title?: string;
  coverArt?: string;
  coverArtURL?: string | null;
  selectedOptionKey?: string;
  CoachName?: string;
  // Cart items are assembled by spreading product documents, which carry more
  // fields than the cart logic itself reads. Allow those through rather than
  // forcing every consumer to redeclare them; tighten as the cart UI migrates.
  [key: string]: unknown;
}

export interface CartState {
  processingCheckout: boolean;
  showCheckout: boolean;
  cartItems: CartItem[];
  totalPrice: number;
}

/* ------------------------------- user slice ------------------------------- */

// The custom claims object assembled in AuthContext diverges slightly from the
// published CustomClaims: it does not set `accessTier`. Model that exactly so
// consumers get real types without `as any`, and so the divergence is visible
// when the auth context itself is migrated. (`boxRole` is declared on the
// published CustomClaims, so no local widening is needed for it.)
export type UserClaims = Omit<CustomClaims, "accessTier"> &
  Partial<Pick<CustomClaims, "accessTier">>;

// The Firestore Users document, spread into state. It is loosely shaped
// (legacy fields, gradual schema growth), so the known fields are typed and the
// rest stay `unknown`. The eventual strict shape lives in
// `@lemonadetraining/contracts` (User); narrow toward it as fields settle.
export interface UserInfoData {
  privacySettings?: Record<string, unknown>;
  [key: string]: unknown;
}

// The gym-grant fields of the own-Users document, read straight off the
// snapshot. A change here is what triggers a forced token refresh.
export interface GrantSignal {
  boxID: string | null;
  boxRole: BoxRole | null;
  boxStatus: BoxMemberStatus | null;
  boxGrantVersion: number | null;
  claimsStale: boolean;
  /** Users.boxLicense is present. */
  boxLicensed: boolean;
}

export interface UserState {
  user: FirebaseUser | null;
  claims: UserClaims | null;
  isLoggedIn: boolean;
  userInfoData: UserInfoData;
  // Mirrors Users/{uid}/Settings/preferences.timeFormat; "system" until read or set.
  timeFormat: TimeFormatPreference;
  // Mirrors Users/{uid}/Settings/preferences.timeZoneDisplay; "gym" until read or set. Never the zone.
  timeZoneDisplay: TimeZoneDisplayPreference;
  // True once this user's saved preferences were read or the read failed.
  preferencesSynced: boolean;
  grantSignal: GrantSignal | null;
}

/* -------------------------- access notices slice -------------------------- */

// createdAt is normalised to epoch millis at ingestion; the rest is the stored
// document as published in @lemonadetraining/contracts.
export type StoredAccessNotice = Omit<AccessNotice, "createdAt" | "readAt"> & {
  id: string;
  createdAt: number;
  readAt: null;
};

export interface AccessNoticesState {
  unread: StoredAccessNotice[];
  listenerFailed: boolean;
  acknowledgedIDs: string[];
}

/* -------------------------------- root ------------------------------------ */

// Keys must match the reducer map in ./appReduxStore.
export interface RootState {
  notify: NotificationState;
  user: UserState;
  cart: CartState;
  accessNotices: AccessNoticesState;
}

/* --------------------------- auth context shape --------------------------- */

// The value provided by AuthContextProvider (see ./auth-context). Method names
// preserve the existing (legacy-spelled) API so consumers keep compiling;
// renaming is a separate, breaking change.
export interface AuthContextShape {
  user: FirebaseUser | null;
  isLoggedIn: boolean;
  loginWithEmail: (email: string, password: string) => Promise<UserCredential>;
  singupWithEmail: (email: string, password: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithApple: () => Promise<UserCredential>;
  userLogOut: () => Promise<void>;
  updateCustomClaims: () => Promise<void>;
  resestPassword: (email: string) => Promise<unknown>;
  updateUserProfile: (fullName: string, profilePicURL: string) => Promise<void>;
  uploadUserProfilePic: (file: Blob) => Promise<string>;
  getProfilePicURL: (userUID: string) => Promise<string | null>;
}

/* ------------------------- callable response shapes ----------------------- */

// Cross-domain auth handoff (consumed when the app shell is migrated).
export interface CreateCrossDomainTokenResult {
  customToken: string;
}

/* ---------------------- Product lifecycle callables ----------------------- */

/*
 * Discontinuing is the deliberate wind-down of a product: it archives the
 * product AND cancels every active subscription to it at the end of the
 * member's paid period. It is a strictly larger action than `archiveProduct`,
 * which only stops new sales and leaves existing members' subscriptions
 * running. The callable takes `{ productUID, productType, boxID?, confirm? }`;
 * only `confirm: true` executes, anything else is a dry run that mutates
 * nothing and reports how many members a confirmed run would affect.
 *
 * The Stripe half can fail independently of the archive half, so both arms
 * carry `stripeWoundDown`. Consumers MUST read it before making any claim
 * about people's subscriptions.
 */

/** Dry-run response (`confirm` absent or false) — nothing was mutated. */
export interface DiscontinueProductPreviewResult {
  preview: true;
  /**
   * How many active subscriptions a confirmed run would cancel. `null` when
   * `stripeWoundDown` is false: the number is genuinely unknown, not zero.
   */
  activeSubscriptionCount: number | null;
  /**
   * False when the product's Stripe account could not be verified. A confirmed
   * run would still delist the product here, but Stripe would keep selling it
   * and every member would stay subscribed and keep being billed.
   */
  stripeWoundDown: boolean;
}

/** Confirmed response — the product is archived and its subscriptions cancelled. */
export interface DiscontinueProductExecutedResult {
  preview: false;
  /** How many active subscriptions were cancelled at their paid-period end. */
  cancelledCount: number;
  /**
   * False when Stripe was never reached: the product is delisted here, but
   * nothing was cancelled, so members are still subscribed and still being
   * billed. `cancelledCount` is then 0 because none were attempted, which is
   * not the same claim as "nobody was subscribed".
   */
  stripeWoundDown: boolean;
}

/**
 * Response contract of the `discontinueProduct` callable. Discriminate on
 * `preview`. Failures throw an `HttpsError` (unauthenticated / invalid-argument
 * / not-found / permission-denied) rather than returning a partial object.
 */
export type DiscontinueProductResult =
  DiscontinueProductPreviewResult | DiscontinueProductExecutedResult;

/**
 * Narrows a `discontinueProduct` response to the dry-run arm. Checks the shape
 * rather than trusting the discriminant alone: this is a cross-service seam, and
 * a response that matches neither arm must be treated as a failure by the caller
 * instead of quietly reading as "0 members affected". An explicit `null` count
 * IS a valid preview (Stripe unreachable); a missing one is not.
 */
export function isDiscontinuePreview(result: unknown): result is DiscontinueProductPreviewResult {
  if (typeof result !== "object" || result === null) return false;
  const candidate = result as Partial<DiscontinueProductPreviewResult>;
  const count = candidate.activeSubscriptionCount;
  return candidate.preview === true && (typeof count === "number" || count === null);
}

/* ----------------------- Stripe Connect callables ------------------------- */

/**
 * The persisted subset of a Stripe Connect `Account` mirrored into the
 * `StripeAccounts/{uid}` Firestore document. Written on onboarding
 * (`accountOnboarding`) and kept in sync by the connected-account webhook
 * (`updateConnectedAccountInfo`) in functions/StripeManagement.js. This is a
 * stored projection — only these fields are persisted, not the full Stripe
 * Account object.
 */
export interface StripeAccountInfo {
  /** Stripe Connect account id, e.g. "acct_1AbC…". */
  id: string;
  /**
   * Account email. Follows Stripe's own nullability — a freshly created
   * Standard account has no email until the connected user provides one.
   */
  email: string | null;
  /** Account creation time as a Unix timestamp in seconds (Stripe `Account.created`). */
  created: number;
  /** Whether the connected account can create charges. */
  charges_enabled: boolean;
  /** Whether payouts are enabled for the connected account. */
  payouts_enabled: boolean;
  /** Whether the account has completed Stripe onboarding. */
  details_submitted: boolean;
}

/**
 * Response contract of the `getStripeAccountInfo` callable — exported from
 * functions/index.js, implemented as `getAccountInfo` in
 * functions/StripeManagement.js. Takes no request payload; returns the caller's
 * own `StripeAccounts/{uid}` document, or `null` when the user has never started
 * Stripe onboarding (the document is absent, so the handler returns `undefined`,
 * which the callable transport encodes as `null`). Consumers must null-check.
 */
export type GetStripeAccountInfoResult = StripeAccountInfo | null;

/**
 * Request contract of the `getBoxStripeAccountStatus` callable — see
 * functions/boxes.js.
 */
export interface GetBoxStripeAccountStatusRequest {
  /** The box whose Stripe-account linkage status is being queried. */
  boxID: string;
}

/**
 * Response contract of the `getBoxStripeAccountStatus` callable — see
 * functions/boxes.js. All three fields are always present on the success path;
 * failures throw an `HttpsError` (unauthenticated / invalid-argument /
 * permission-denied / not-found / internal) rather than returning a partial
 * object.
 */
export interface GetBoxStripeAccountStatusResult {
  /** True when the box has a linked Stripe connected account. */
  hasStripeAccount: boolean;
  /** The linked Stripe account id, or `null` when none is linked. */
  stripeAccountID: string | null;
  /**
   * Whether the box is treated as payment-active. Today this mirrors
   * `hasStripeAccount` exactly (presence of an account id); the server does not
   * yet re-verify activation against the Stripe API.
   */
  isActive: boolean;
}
